<script setup lang="ts">
// Chrome shared by the public (unauthenticated) pages — logo left, title centred, language switcher
// right, then the page content and the footer. These pages render the same for anonymous and
// logged-in visitors, so they deliberately do not use MainMenu.
//
// Kept dumb on purpose: the app passes its own logo asset and title component through slots and owns
// the locale change, so this component needs no app-local imports.
import { computed } from "vue";
import Button from "primevue/button";
import SpeedDial from "primevue/speeddial";
import Footer from "./Footer.vue";

const props = withDefaults(
    defineProps<{
        /** Currently active locale, e.g. "de". */
        activeLocale: string;
        /** Offered locales; public pages are DE/EN only. */
        locales?: string[];
        /**
         * Id prefix for the switcher, so each page keeps stable e2e selectors:
         * `<prefix>-speed-dial` and `<prefix>_btn_<LOCALE>`.
         */
        idPrefix?: string;
        switcherLabel?: string;
        compactHeader?: boolean;
    }>(),
    {
        locales: () => ["en", "de"],
        idPrefix: "public-page",
        switcherLabel: "",
        compactHeader: false,
    },
);

const emit = defineEmits<{ localeChange: [locale: string] }>();

const LOCALE_NAMES: Record<string, string> = { en: "English", de: "Deutsch" };

function localeName(code: string): string {
    return LOCALE_NAMES[code] ?? code.toUpperCase();
}

const languageItems = computed(() =>
    props.locales.map((code) => ({
        label: code.toUpperCase(),
        code,
        selected: code.toLowerCase() === props.activeLocale.toLowerCase(),
        command: () => emit("localeChange", code),
    })),
);
</script>

<template>
    <div class="public-page">
        <div class="header-container" :class="{ compact: compactHeader }">
            <div class="logo-container">
                <slot name="logo" />
            </div>

            <div class="title-container">
                <slot name="title" />
            </div>

            <div class="language-selector-container">
                <SpeedDial
                    :id="`${idPrefix}-speed-dial`"
                    :model="languageItems"
                    show-icon="pi pi-globe"
                    type="linear"
                    direction="down"
                    button-class="p-button-icon-only p-button-text"
                    :aria-label="switcherLabel"
                >
                    <template #item="{ item, onClick }">
                        <Button
                            :id="`${idPrefix}_btn_${item.label}`"
                            tabindex="-1"
                            rounded
                            :aria-label="localeName(item.code)"
                            v-tooltip.left="localeName(item.code)"
                            :class="item.selected ? 'bg-brand text-white' : 'text-brand bg-light'"
                            :pt="{
                                root: {
                                    style: {
                                        border: 'none',
                                        fontSize: '0.75em',
                                        padding: '0.75em',
                                        minWidth: '3em',
                                        fontWeight: 'bold',
                                        background: item.selected ? 'var(--p-primary-500)' : 'var(--p-primary-50)',
                                    },
                                },
                            }"
                            @click="onClick"
                        >
                            <span class="lang">{{ item.label }}</span>
                        </Button>
                    </template>
                </SpeedDial>
            </div>
        </div>

        <slot />
    </div>
    <Footer />
</template>

<style scoped>
.public-page {
    max-width: 1400px;
    margin: 0 auto;
    padding: 2rem 1rem;
}

.header-container {
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin-bottom: 2rem;
    min-height: 60px;
}

.header-container.compact {
    margin-bottom: 1.25rem;
}

.logo-container {
    position: absolute;
    left: 0;
    top: 0;
    flex-shrink: 0;
}

.title-container {
    flex: 0 0 auto;
    text-align: center;
    max-width: calc(100% - 200px);
}

.language-selector-container {
    position: absolute;
    right: 0;
    top: 0;
    flex-shrink: 0;
    align-self: flex-start;
}

.bg-light {
    background-color: var(--p-surface-0);
}

:deep(.lang) {
    transition: color 0.2s ease;
}
</style>
